<template>
  <div v-if="sessions.length === 0" class="session-empty">
    <span class="session-empty-text">
      {{ isQueue ? '目前沒有排隊中的客戶' : '目前沒有進行中的會話' }}
    </span>
  </div>

  <div v-else class="session-list">
    <div v-if="isQueue && onPick" class="queue-pick-bar">
      <el-button type="primary" class="queue-pick-btn" icon="el-icon-download" @click="onPick">
        接入下一個排隊客戶
      </el-button>
    </div>

    <div
      v-for="session in sessions"
      :key="session.id"
      class="session-item"
      :class="{ selected: selectedId === session.id }"
      @click="$emit('select', session.id)"
    >
      <div class="session-avatar-wrap">
        <el-badge :value="session.unreadCount || 0" :hidden="!session.unreadCount">
          <el-avatar :size="40" class="session-avatar" :class="{ selected: selectedId === session.id }">
            <i class="el-icon-user"></i>
          </el-avatar>
        </el-badge>
        <span class="session-status-dot" :style="{ backgroundColor: statusDotColor(session.status) }"></span>
      </div>

      <div class="session-info">
        <div class="session-info-top">
          <div class="session-name-block">
            <div class="session-name">{{ safeName(session) }}</div>
            <div v-if="session.customerId" class="session-cid">ID: {{ session.customerId }}</div>
          </div>
          <span class="session-time">{{ formatTime(session.lastMessageTime) }}</span>
        </div>
        <div class="session-tags">
          <el-tag v-if="session.agentNickname" size="small" class="mini-tag" type="info">
            客服 {{ session.agentNickname }}
          </el-tag>
          <el-tag size="small" class="mini-tag" :type="statusTagType(session.status)">
            {{ sessionStatusMap[session.status] || '未知' }}
          </el-tag>
          <el-tag
            v-if="session.clientSource"
            size="small"
            class="mini-tag"
            :type="tagTypeOf(srcMeta(session.clientSource).color)"
          >
            {{ srcMeta(session.clientSource).label }}
          </el-tag>
          <el-tag size="small" class="mini-tag" type="info" effect="plain">
            {{ session.channel }}
          </el-tag>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const SessionStatusMap = {
  0: '等待中',
  1: 'AI服務中',
  2: '人工服務中',
  3: '已關閉',
  4: '已超時'
}

const ClientSourceMap = {
  OTHER: { label: '其他', color: '#909399' },
  web: { label: 'Web', color: '#409eff' },
  mini_program: { label: '小程序', color: '#67c23a' },
  app: { label: 'App', color: '#e6a23c' },
  wechat: { label: '微信', color: '#67c23a' },
  phone: { label: '電話', color: '#f56c6c' }
}

const COLOR_TAG_TYPE = {
  '#409eff': '',
  '#67c23a': 'success',
  '#e6a23c': 'warning',
  '#f56c6c': 'danger',
  '#909399': 'info'
}

export default {
  name: 'SessionListItem',
  props: {
    sessions: { type: Array, default: () => [] },
    selectedId: { type: [Number, String], default: null },
    isQueue: { type: Boolean, default: false },
    agentId: { type: [Number, String], default: null },
    onRefresh: { type: Function, default: null },
    onPick: { type: Function, default: null }
  },
  data() {
    return {
      sessionStatusMap: SessionStatusMap,
      clientSourceMap: ClientSourceMap
    }
  },
  methods: {
    // 游客可能 customerName / customerId 都为 null，避免展示成 "客户 null"
    safeName(session) {
      const name = session && session.customerName
      if (name) return name
      const cid = session && session.customerId
      if (cid) return '客戶 ' + cid
      return '客戶'
    },
    formatTime(ts) {
      if (!ts) return ''
      const d = new Date(String(ts).endsWith('Z') || String(ts).includes('T') ? ts : ts)
      let date = new Date(ts)
      if (isNaN(date.getTime())) date = new Date()
      const hh = String(date.getHours()).padStart(2, '0')
      const mm = String(date.getMinutes()).padStart(2, '0')
      return hh + ':' + mm
    },
    statusDotColor(status) {
      if (status === 2) return '#52c41a'
      if (status === 0) return '#faad14'
      if (status === 1) return '#1677ff'
      return '#d9d9d9'
    },
    statusTagType(status) {
      if (status === 2) return 'success'
      if (status === 0) return 'warning'
      if (status === 1) return 'primary'
      return 'info'
    },
    srcMeta(clientSource) {
      return this.clientSourceMap[clientSource] || this.clientSourceMap.OTHER
    },
    tagTypeOf(color) {
      return COLOR_TAG_TYPE[color] || ''
    }
  }
}
</script>

<style scoped>
.session-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: #c0c4cc;
  padding: 24px;
}
.session-empty-text {
  color: #909399;
  font-size: 14px;
}
.session-list {
  flex: 1;
  overflow: auto;
}
.queue-pick-bar {
  padding: 8px 12px;
}
.queue-pick-btn {
  width: 100%;
}
.session-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  border-left: 3px solid transparent;
  background: transparent;
  transition: all 0.15s;
}
.session-item:hover {
  background: #f0f2f5;
}
.session-item.selected {
  border-left-color: #4f46e5;
  background: #f0f0ff;
}
.session-avatar-wrap {
  position: relative;
  flex-shrink: 0;
}
.session-avatar {
  background: #f0f2f5;
}
.session-avatar.selected {
  background: #4f46e5;
  color: #fff;
}
.session-status-dot {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #fff;
}
.session-info {
  flex: 1;
  min-width: 0;
}
.session-info-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}
.session-name-block {
  min-width: 0;
}
.session-name {
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.session-cid {
  font-size: 11px;
  color: #c0c4cc;
  line-height: 16px;
}
.session-time {
  font-size: 11px;
  color: #c0c4cc;
  flex-shrink: 0;
  margin-left: 8px;
}
.session-tags {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.mini-tag {
  font-size: 11px;
  line-height: 18px;
  padding: 0 6px;
  height: 20px;
}
</style>